import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import "./VehicleImageLightbox.css";

import { image_url } from "../Utils/connection_strings";

/** Returns displayable URL strings from API `vehicleImage` (strings or `{ imageUrl }`). */
export function normalizeVehicleImageUrls(vehicleImage) {
  if (!Array.isArray(vehicleImage)) return [];
  const clean_image_url = image_url?.endsWith("/") ? image_url : `${image_url}/`;
  return vehicleImage
    .filter((item) => {
      if (item == null || item === "") return false;
      if (typeof item === "string") return item.trim().length > 0;
      return Boolean(item?.imageUrl);
    })
    .map((item) => {
      const urlStr = typeof item === "string" ? item.trim() : item.imageUrl;
      if (urlStr.startsWith("http://") || urlStr.startsWith("https://")) {
        return urlStr;
      }
      const cleanPath = urlStr.startsWith("/") ? urlStr.slice(1) : urlStr;
      return `${clean_image_url}${cleanPath}`;
    });
}

export default function VehicleImageLightbox({
  show,
  urls,
  startIndex,
  onHide,
}) {
  const [current, setCurrent] = useState(0);
  const [zoom, setZoom] = useState(1);

  const list = Array.isArray(urls) ? urls : [];
  const hasMultiple = list.length > 1;

  useEffect(() => {
    if (show && list.length > 0) {
      const i = Math.min(
        Math.max(0, startIndex ?? 0),
        list.length - 1
      );
      setCurrent(i);
      setZoom(1);
    }
  }, [show, startIndex, list.length]);

  const goPrev = useCallback(() => {
    if (list.length < 2) return;
    setCurrent((c) => (c - 1 + list.length) % list.length);
    setZoom(1);
  }, [list.length]);

  const goNext = useCallback(() => {
    if (list.length < 2) return;
    setCurrent((c) => (c + 1) % list.length);
    setZoom(1);
  }, [list.length]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") onHide();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, goPrev, goNext, onHide]);

  if (!list.length) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      fullscreen="lg-down"
      dialogClassName="vehicle-image-lightbox-dialog"
      contentClassName="vehicle-image-lightbox-content"
    >
      <Modal.Header
        closeButton
        closeVariant="dark"
        className="vehicle-image-lightbox-header"
      >
        <Modal.Title as="div" className="w-100 mb-0">
          <div className="vehicle-image-lightbox-title-wrap">
            <p className="title-text mb-0">
              <i className="fas fa-images" aria-hidden />
              Vehicle images
            </p>
            <span className="vehicle-image-lightbox-count">
              {current + 1} / {list.length}
            </span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="vehicle-image-lightbox-stage d-flex align-items-center justify-content-center position-relative p-0">
        {hasMultiple && (
          <button
            type="button"
            className="btn vehicle-image-lightbox-nav-btn position-absolute start-0 top-50 translate-middle-y ms-2 ms-sm-3"
            onClick={goPrev}
            aria-label="Previous image"
          >
            <i className="fas fa-chevron-left" />
          </button>
        )}
        <div className="vehicle-image-lightbox-img-wrap mx-2 mx-sm-0">
          <img
            src={list[current]}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "76vh",
              objectFit: "contain",
              transform: `scale(${zoom})`,
              transformOrigin: "center center",
              transition: "transform 0.12s ease-out",
            }}
            draggable={false}
          />
        </div>
        {hasMultiple && (
          <button
            type="button"
            className="btn vehicle-image-lightbox-nav-btn position-absolute end-0 top-50 translate-middle-y me-2 me-sm-3"
            onClick={goNext}
            aria-label="Next image"
          >
            <i className="fas fa-chevron-right" />
          </button>
        )}
      </Modal.Body>
      <Modal.Footer className="vehicle-image-lightbox-footer">
        <div className="vehicle-image-lightbox-toolbar">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="vehicle-image-lightbox-zoom-label">Zoom</span>
            <div
              className="vehicle-image-lightbox-zoom-group"
              role="group"
              aria-label="Zoom controls"
            >
              <button
                type="button"
                className="zoom-icon-btn"
                title="Zoom out"
                onClick={() =>
                  setZoom((z) =>
                    Math.max(0.5, Math.round((z - 0.25) * 100) / 100)
                  )
                }
              >
                <i className="fas fa-minus" aria-hidden />
              </button>
              <button type="button" onClick={() => setZoom(1)}>
                Reset
              </button>
              <button
                type="button"
                className="zoom-icon-btn"
                title="Zoom in"
                onClick={() =>
                  setZoom((z) =>
                    Math.min(3, Math.round((z + 0.25) * 100) / 100)
                  )
                }
              >
                <i className="fas fa-plus" aria-hidden />
              </button>
            </div>
          </div>
          <p className="vehicle-image-lightbox-hint mb-0">
            <kbd>←</kbd> <kbd>→</kbd> images · <kbd>Esc</kbd> close
          </p>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
